import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { MessageCircle } from 'lucide-react';

const SelfDriveEnquiry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { vehicle, city, from, to } = location?.state || {};

  const [formData, setFormData] = useState({
    name: '',
    countryCode: '+91',
    whatsappNumber: '',
    pickupDate: '',
    pickupTime: '',
    pickupAddress: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!vehicle || (!city && !from)) {
      navigate('/self-drive');
    }
  }, [vehicle, city, from, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWhatsAppEnquiry = () => {
    const { name, countryCode, whatsappNumber, pickupDate, pickupTime, pickupAddress } = formData;
    
    // Validate required fields
    if (!name || !whatsappNumber || !pickupDate || !pickupTime) {
      alert('Please fill in all required fields');
      return;
    }

    // Create WhatsApp message
    const whatsappMessage = `*Self Drive Car Rental Enquiry*%0A%0A` +
      `*Booking Details:*%0A` +
      `Going From: ${from || city}%0A` +
      `Going To: ${to || 'Not specified'}%0A` +
      `Selected Car: ${vehicle?.type || vehicle?.name || 'Not specified'}%0A` +
      `Price: ₹${vehicle?.price?.toLocaleString('en-IN')}/- Inc. of Taxes*%0A%0A` +
      `*Contact:*%0A` +
      `Name: ${name}%0A` +
      `Contact Number: ${countryCode} ${whatsappNumber}%0A%0A` +
      `*Pickup Details:*%0A` +
      `Pickup Date: ${pickupDate}%0A` +
      `Pickup Time: ${pickupTime}%0A` +
      `Pickup Address: ${pickupAddress || 'Not specified'}%0A%0A` +
      `Please confirm availability and booking details.`;

    // WhatsApp number
    const whatsappBusinessNumber = '917998666660';
    const whatsappUrl = `https://wa.me/${whatsappBusinessNumber}?text=${whatsappMessage}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  if (!vehicle || (!city && !from)) {
    return null;
  }

  return (
    <>
      <SEO
        title={`Enquiry for ${vehicle?.name} - Self Drive Car Rental ${city} | Advait Tour and Travels`}
        description={`Send enquiry for ${vehicle?.name} self-drive car rental in ${city}. Book now and enjoy flexible rental options.`}
        keywords={`${vehicle?.name} rental ${city}, self drive car enquiry, book ${vehicle?.name}`}
        canonicalUrl={`${window.location?.origin}/self-drive-enquiry`}
        ogTitle={`Enquiry for ${vehicle?.name} - Self Drive Car Rental ${city} | Advait Tour and Travels`}
        ogDescription={`Send enquiry for ${vehicle?.name} self-drive car rental in ${city}. Book now and enjoy flexible rental options.`}
        ogUrl={`${window.location?.origin}/self-drive-enquiry`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": vehicle?.name,
          "offers": {
            "@type": "Offer",
            "price": vehicle?.price,
            "priceCurrency": "INR"
          }
        }}
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <Header />
        
        <main className="py-24 px-4">
          <div className="container mx-auto max-w-2xl">
            {/* Booking Details Card */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Your Booking Details
              </h1>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Going From:</span>
                  <span className="text-gray-900 font-semibold">{from || city}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Going To:</span>
                  <span className="text-gray-900 font-semibold">{to || 'Not specified'}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Selected Car:</span>
                  <span className="text-gray-900 font-semibold">{vehicle?.type || vehicle?.name || 'Not specified'}</span>
                </div>
                
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-700 font-medium">Price:</span>
                  <span className="text-gray-900 font-bold text-lg">₹{vehicle?.price?.toLocaleString('en-IN')}/- Inc. of Taxes*</span>
                </div>
              </div>
            </div>

            {/* Contact & Pickup Details Form */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Contact & Pickup Details
              </h2>
              
              <form className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData?.name}
                    onChange={handleChange}
                    placeholder="Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                {/* WhatsApp Number with Country Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="countryCode"
                      value={formData?.countryCode}
                      onChange={handleChange}
                      className="w-24 px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                    >
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+61">+61</option>
                      <option value="+971">+971</option>
                      <option value="+65">+65</option>
                      <option value="+86">+86</option>
                    </select>
                    <input
                      type="tel"
                      name="whatsappNumber"
                      value={formData?.whatsappNumber}
                      onChange={handleChange}
                      placeholder="Contact Number..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Date and Time Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Date *
                    </label>
                    <input
                      type="date"
                      name="pickupDate"
                      value={formData?.pickupDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time
                    </label>
                    <input
                      type="time"
                      name="pickupTime"
                      value={formData?.pickupTime}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Pickup Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pick up Address *
                  </label>
                  <input
                    type="text"
                    name="pickupAddress"
                    value={formData?.pickupAddress}
                    onChange={handleChange}
                    placeholder="Pick up Address *"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                {/* Send Enquiry WhatsApp Button */}
                <button
                  type="button"
                  onClick={handleWhatsAppEnquiry}
                  className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-3 text-base shadow-md hover:shadow-lg mt-6"
                >
                  <MessageCircle size={22} />
                  Send Enquiry WhatsApp
                </button>
              </form>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default SelfDriveEnquiry;